import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTypedQuery } from 'src/utils/hooks/useTypedQuery';
import { GetImportColumnsDocument, ImportColumnDataFragment } from './__generated__/ImportColumns.graphql';
import {
  CommonFieldDefinitionDataFragment,
  GetFieldMetadataForPathsDocument
} from './__generated__/FieldMetaDataForPaths.graphql';
import { ImportEntityTypeModel } from 'src/graphql/types';
import { FieldMetadataElementValidator } from 'src/components/fieldMetadata/FieldMetadataElementValidator';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import { AxiosRequestConfig } from 'utils/gql/axios/AxiosClient';
import { EngagementManagingSourceModel, NotificationModel } from '@utmost.co/enterprise-models';
import { post } from 'src/utils/rest-clients';
import { isAxiosError } from 'src/utils/gql/axios/axiosUtils';
import { FETCH_POLICY } from 'src/components/common/apollo/FETCH_POLICY';
import { Data, stringUtil } from '@beeline.com/react-spreadsheet-import';

export interface AllowedImportType {
  id: 'WORKER-RETROACTIVE-CHANGE' | string;
  entityType: ImportEntityTypeModel;
  label?: string;
}

const options = { fetchPolicy: FETCH_POLICY.NO_CACHE };

export type ImportColumnData = Omit<ImportColumnDataFragment, '__typename'>;
export type CommonFieldDefinitionData = Omit<CommonFieldDefinitionDataFragment, '__typename'>;

function constructImportFilename(filename: string | undefined | null): string {
  if (filename === undefined || filename === null) return '';

  const fileParts = filename.split('.');
  const extension = fileParts[fileParts.length - 1] ?? '';

  if (extension === 'csv' || extension === undefined) {
    return filename;
  } else {
    return `${filename}.csv`;
  }
}

export const useImporterData = (defaultImportType: AllowedImportType | undefined) => {
  const [importType, setImportTypeState] = useState<AllowedImportType | undefined>(defaultImportType);

  const {
    get: getColumns,
    data: importColumns,
    loading: loadingImportColumns
  } = useTypedQuery(GetImportColumnsDocument, options);
  const {
    get: getFieldMetadataPaths,
    data: fieldMetadataForPaths,
    loading: loadingFieldMetadata
  } = useTypedQuery(GetFieldMetadataForPathsDocument, options);

  const { showError } = useSnackbar();
  const { customManagingSourceIcons } = useDarkly();

  const setImportType = useCallback(
    async (importType: AllowedImportType | undefined) => {
      setImportTypeState(importType);

      if (!importType) {
        return;
      }

      const response = await getColumns({
        variables: { entityType: importType.entityType as ImportEntityTypeModel, importTypeId: importType?.id },
        context: { batch: false }
      });
      const columnUrns = response.data?.importColumns?.columns?.map((el) => el?.urn ?? '');

      getFieldMetadataPaths({ variables: { pathUrns: columnUrns }, context: { batch: false } });
    },
    [getFieldMetadataPaths, getColumns]
  );

  const setImportTypeRef = useRef(setImportType);
  setImportTypeRef.current = setImportType;

  const defaultImportTypeRef = useRef(defaultImportType);
  defaultImportTypeRef.current = defaultImportType;

  // biome-ignore lint/correctness/useExhaustiveDependencies: id is used as a trigger, not read directly — refs handle stale closure
  useEffect(() => {
    if (defaultImportTypeRef.current) {
      setImportTypeRef.current(defaultImportTypeRef.current);
    }
  }, [defaultImportType?.id]);

  const columns: ImportColumnData[] = useMemo(() => {
    return (importColumns?.importColumns.columns || []) as ImportColumnData[];
  }, [importColumns]);

  const fieldMetadataValidator = useMemo(() => {
    return new FieldMetadataElementValidator(
      (fieldMetadataForPaths?.fieldMetadataForPaths?.definitions ?? []) as DeeplyNotNull<CommonFieldDefinitionData[]>
    );
  }, [fieldMetadataForPaths]);

  const urnToFieldMetadataDefinitionMap: Map<string, CommonFieldDefinitionData> = useMemo(() => {
    return new Map(
      fieldMetadataForPaths?.fieldMetadataForPaths?.definitions?.map((el) => [
        el?.urn || '',
        el as CommonFieldDefinitionData
      ]) ?? []
    );
  }, [fieldMetadataForPaths]);

  const onImport = useCallback(
    async (
      data: Data<string>[] | undefined,
      fileName: string | null,
      comment: string,
      managingSource: EngagementManagingSourceModel | undefined,
      onImportComplete?: () => void,
      discardPendingChangesets?: boolean
    ) => {
      if (!importType || !managingSource) return;
      try {
        const encoder = new TextEncoder();
        const importFilename = constructImportFilename(fileName);
        const importDataBytes = encoder.encode(JSON.stringify(normalizeData(data ?? [])));
        const importDataBlob = new Blob([importDataBytes], { type: 'application/octet-stream' });

        const formData = new FormData();
        const managingSourceCorrected =
          managingSource === 'WORKDAY' && customManagingSourceIcons ? 'workday' : managingSource;

        formData.set('importData', importDataBlob);
        formData.set('importFilename', importFilename);
        formData.set('importEntityType', importType.entityType);
        formData.set('sourceName', managingSourceCorrected);
        formData.set('importTypeId', importType.id);
        if (discardPendingChangesets) {
          formData.set('discardPendingChangesets', new Blob(['true'], { type: 'application/json' }));
        }
        if (comment) {
          formData.set('comment', comment);
        }

        const config: AxiosRequestConfig = {
          headers: {
            'content-type': 'multipart/form-data',
            'content-encoding': 'gzip'
          }
        };

        await post<NotificationModel>('/importData', formData, config);
      } catch (e: unknown) {
        const message = isAxiosError<{ errors?: { code: string; message: string }[] }>(e)
          ? e?.response?.data?.errors?.[0]?.message
          : (e as Error).toString();
        showError(`Failed to import: ${message}`);
        // throw an error to display it inside docyard component
        throw new Error(message);
      } finally {
        onImportComplete?.();
      }
    },
    [showError, importType, customManagingSourceIcons]
  );

  return {
    setImportType,
    onImport,
    importType,
    importColumns: columns,
    urnToFieldMetadataDefinitionMap,
    fieldMetadataValidator,
    hasFieldMetadataForPaths: !!fieldMetadataForPaths,
    loading: loadingImportColumns || loadingFieldMetadata
  };
};
const multiValueJoiner = (values: string[]) => {
  // replace "value,withComma" with "value\,withComma"
  return values.map(stringUtil.unEscapedToEscaped).join(',');
};

function normalizeData(data: Data<string>[]): Data<string>[] {
  return data.map((it) => {
    return Object.entries(it).reduce<Data<string>>((acc, curr) => {
      const [key, value] = curr;
      if (Array.isArray(value)) {
        acc[key] = multiValueJoiner(value);
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});
  });
}
